import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { getAthletesAggregate, AthleteAggregate } from "../firebase/athletes";
import {
  CurrentAthleteMembership,
  getCoachOrganizationAccess,
  getCurrentAthleteMemberships,
  getOrganizations,
  Organization,
  requestClubTransfer,
} from "../firebase/organizationFoundation";
import { useTranslation } from "../hooks/useTranslation";
import "./CoachClub.css";

export default function CoachClub() {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [rosterOrganizationIds, setRosterOrganizationIds] = useState<string[]>([]);
  const [memberships, setMemberships] = useState<CurrentAthleteMembership[]>([]);
  const [athletes, setAthletes] = useState<AthleteAggregate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [working, setWorking] = useState(false);
  const [transfer, setTransfer] = useState({ athleteId: "", toOrganizationId: "", reason: "" });

  useEffect(() => {
    if (!currentUser) return;
    let active = true;
    Promise.all([getCoachOrganizationAccess(currentUser.uid), getOrganizations(), getAthletesAggregate()])
      .then(async ([access, organizationRows, athleteRows]) => {
        const membershipRows = await getCurrentAthleteMemberships(access.rosterOrganizationIds);
        if (!active) return;
        setRosterOrganizationIds(access.rosterOrganizationIds);
        setOrganizations(organizationRows);
        setAthletes(athleteRows);
        setMemberships(membershipRows);
      })
      .catch((loadError) => {
        console.error("Coach club access load failed", loadError);
        if (active) setError(t("myClub.loadError"));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [currentUser, t]);

  const athleteMap = useMemo(() => new Map(athletes.map((athlete) => [athlete.athleteId, athlete])), [athletes]);
  const myOrganizations = organizations.filter((organization) => rosterOrganizationIds.includes(organization.id));
  const destinationOrganizations = organizations.filter((organization) => organization.status === "ACTIVE" && !organization.external);

  const submitTransfer = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!transfer.athleteId || !transfer.toOrganizationId || transfer.reason.trim().length < 8) return;
    setWorking(true); setError(""); setMessage("");
    try {
      await requestClubTransfer(transfer);
      setMessage(t("myClub.transferRequested"));
      setTransfer({ athleteId: "", toOrganizationId: "", reason: "" });
    } catch (requestError) {
      console.error("Transfer request failed", requestError);
      setError(t("myClub.transferError"));
    } finally { setWorking(false); }
  };

  return <div className="page-content"><Navbar /><main className="coach-club-shell">
    <header className="coach-club-header"><div><p className="coach-club-eyebrow">{t("myClub.eyebrow")}</p><h1>{t("myClub.title")}</h1><p>{t("myClub.subtitle")}</p></div></header>
    {error && <div className="coach-club-error">{error}</div>}
    {message && <div className="coach-club-notice">{message}</div>}
    {loading ? <div className="coach-club-empty">{t("myClub.loading")}</div> : myOrganizations.length === 0 ? <div className="coach-club-empty"><strong>{t("myClub.noAccessTitle")}</strong><p>{t("myClub.noAccessHelp")}</p></div> : <>
      <div className="coach-club-grid">{myOrganizations.map((organization) => <article className="coach-club-card" key={organization.id}><h2>{organization.officialName}</h2><p>{organization.countryName || organization.countryCode}</p><small>{t("myClub.rosterAccess")}</small></article>)}</div>
      <section className="coach-club-section"><header><h2>{t("myClub.athletes")}</h2><span>{memberships.length} {t("myClub.currentMembers")}</span></header>
        {memberships.length === 0 ? <div className="coach-club-empty">{t("myClub.noAthletes")}</div> : <div className="coach-club-list">{memberships.map((membership) => {
          const athlete = athleteMap.get(membership.athleteId);
          const name = athlete ? `${athlete.firstName} ${athlete.lastName}` : membership.athleteId;
          return <article className="coach-athlete" key={membership.id}>
            <div className="coach-athlete-avatar">{name.charAt(0)}</div>
            <div><strong>{name}</strong><small>{membership.athleteId} · {membership.organizationName}</small></div>
          </article>;
        })}</div>}
      </section>
      <section className="coach-club-section"><header><div><h2>{t("myClub.transferTitle")}</h2><span>{t("myClub.transferHelp")}</span></div></header>
        <form className="coach-transfer-form" onSubmit={submitTransfer}>
          <select value={transfer.athleteId} onChange={(event) => setTransfer((value) => ({ ...value, athleteId: event.target.value }))} required><option value="">{t("myClub.chooseAthlete")}</option>{memberships.map((membership) => { const athlete = athleteMap.get(membership.athleteId); return <option key={membership.id} value={membership.athleteId}>{athlete ? `${athlete.firstName} ${athlete.lastName}` : membership.athleteId}</option>; })}</select>
          <select value={transfer.toOrganizationId} onChange={(event) => setTransfer((value) => ({ ...value, toOrganizationId: event.target.value }))} required><option value="">{t("myClub.chooseNewClub")}</option>{destinationOrganizations.map((organization) => <option key={organization.id} value={organization.id}>{organization.officialName}</option>)}</select>
          <input value={transfer.reason} minLength={8} maxLength={500} onChange={(event) => setTransfer((value) => ({ ...value, reason: event.target.value }))} placeholder={t("myClub.reason")} required />
          <button disabled={working}>{working ? t("myClub.sending") : t("myClub.requestTransfer")}</button>
        </form>
      </section>
    </>}
  </main></div>;
}
